import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import commissionService from '@/services/commissionService';
import type { CommissionDashboard, CommissionPeriod, CommissionSuperAdminDashboard } from '@/types/commission';
import { formatCurrency, formatPeriod, getCommissionStatusLabel, getCommissionStatusColor } from '@/types/commission';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DollarSign, TrendingUp, Clock, CheckCircle, Settings, BarChart3, Calculator, RefreshCw, Search, X, List, LayoutGrid, User, Calendar, FileText, Euro, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { CommissionDetail } from '@/types/commission';

export default function CommissionDashboardPage() {
  const { user } = useAuthStore();
  const location = useLocation();
  const [dashboard, setDashboard] = useState<CommissionDashboard | null>(null);
  const [superAdminDashboard, setSuperAdminDashboard] = useState<CommissionSuperAdminDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [calcSuccess, setCalcSuccess] = useState<string | null>(null);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [calcYear, setCalcYear] = useState(new Date().getFullYear());
  const [calcMonth, setCalcMonth] = useState(new Date().getMonth() === 0 ? 12 : new Date().getMonth()); // Previous month by default

  // Apporteur table state
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'list'>('table');
  const [selectedApporteur, setSelectedApporteur] = useState<any | null>(null);
  const [apporteurPeriods, setApporteurPeriods] = useState<CommissionPeriod[]>([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [expandedPeriod, setExpandedPeriod] = useState<number | null>(null);
  const [periodDetails, setPeriodDetails] = useState<{ [key: number]: CommissionDetail[] }>({});
  const [loadingDetails, setLoadingDetails] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [validationNotes, setValidationNotes] = useState<{ [key: number]: string }>({});
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [periodStatusFilter, setPeriodStatusFilter] = useState<string>('all');
  const [periodYearFilter, setPeriodYearFilter] = useState<number | 'all'>('all');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState<number | null>(null);
  const [apporteurViewMode, setApporteurViewMode] = useState<'list' | 'table'>('list');
  const [selectedPeriodForDetails, setSelectedPeriodForDetails] = useState<CommissionPeriod | null>(null);
  const [selectedPeriodDetails, setSelectedPeriodDetails] = useState<CommissionDetail[]>([]);
  const [loadingSelectedDetails, setLoadingSelectedDetails] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Navigation tabs for super_admin (Validation tab hidden - actions are in popup now)
  const navigationTabs = [
    {
      name: 'Dashboard',
      href: '/commissions/dashboard',
      icon: BarChart3,
      current: location.pathname === '/commissions/dashboard'
    },
    ...(isSuperAdmin ? [{
      name: 'Configuration',
      href: '/commissions/config',
      icon: Settings,
      current: location.pathname === '/commissions/config'
    }] : [])
    // Validation tab removed - actions are now in the apporteur popup
  ];

  useEffect(() => {
    loadDashboard();
  }, [user]);

  const loadDashboard = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      if (isSuperAdmin) {
        // Super admin sees the global dashboard
        const data = await commissionService.getSuperAdminDashboard();
        setSuperAdminDashboard(data);
      } else {
        // Regular admin/apporteur sees their own dashboard
        const data = await commissionService.getAdminDashboard(user.id);
        setDashboard(data);
      }
    } catch (err: any) {
      console.error('Error loading commission dashboard:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateCommissions = async () => {
    try {
      setCalculating(true);
      setError(null);
      setShowCalcModal(false);
      const result = await commissionService.calculateCommissions({
        year: calcYear,
        month: calcMonth
      });
      const periodLabel = `${monthNames[calcMonth - 1]} ${calcYear}`;
      setCalcSuccess(`Commissions calculées pour ${periodLabel}: ${result.periods_created} période(s), ${result.total_amount?.toFixed(2) || 0} € total`);
      setTimeout(() => setCalcSuccess(null), 8000);
      // Reload dashboard
      await loadDashboard();
    } catch (err: any) {
      console.error('Error calculating commissions:', err);
      setError(err.response?.data?.detail || 'Erreur lors du calcul des commissions');
    } finally {
      setCalculating(false);
    }
  };

  // Handle apporteur click to show popup
  const handleApporteurClick = async (admin: any) => {
    setSelectedApporteur(admin);
    setLoadingPeriods(true);
    setApporteurPeriods([]);
    setExpandedPeriod(null);
    setPeriodDetails({});

    try {
      const periods = await commissionService.listPeriods({ admin_id: admin.admin_id });
      setApporteurPeriods(periods);
    } catch (err) {
      console.error('Error loading apporteur periods:', err);
    } finally {
      setLoadingPeriods(false);
    }
  };

  // Toggle period details
  const togglePeriodDetails = async (periodId: number) => {
    if (expandedPeriod === periodId) {
      setExpandedPeriod(null);
      return;
    }

    setExpandedPeriod(periodId);

    if (!periodDetails[periodId]) {
      setLoadingDetails(periodId);
      try {
        const details = await commissionService.getPeriodDetails(periodId);
        setPeriodDetails(prev => ({ ...prev, [periodId]: details }));
      } catch (err) {
        console.error('Error loading period details:', err);
      } finally {
        setLoadingDetails(null);
      }
    }
  };

  // Filter admins by search term
  const filteredAdmins = superAdminDashboard?.admins_summary.filter(admin =>
    admin.admin_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.admin_email?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Action handlers for validate, pay, cancel
  const handleValidate = async (periodId: number) => {
    try {
      setActionLoading(periodId);
      await commissionService.validatePeriod(periodId, { validation_notes: validationNotes[periodId] });
      setActionSuccess('Commission validée avec succès');
      setTimeout(() => setActionSuccess(null), 3000);
      // Refresh periods
      if (selectedApporteur) {
        const periods = await commissionService.listPeriods({ admin_id: selectedApporteur.admin_id });
        setApporteurPeriods(periods);
      }
      // Refresh dashboard
      await loadDashboard();
    } catch (err: any) {
      console.error('Error validating commission:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePay = async (periodId: number) => {
    try {
      setActionLoading(periodId);
      await commissionService.payPeriod(periodId, { payment_notes: validationNotes[periodId] });
      setActionSuccess('Commission marquée comme payée');
      setTimeout(() => setActionSuccess(null), 3000);
      // Refresh periods
      if (selectedApporteur) {
        const periods = await commissionService.listPeriods({ admin_id: selectedApporteur.admin_id });
        setApporteurPeriods(periods);
      }
      // Refresh dashboard
      await loadDashboard();
    } catch (err: any) {
      console.error('Error paying commission:', err);
      setError(err.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (periodId: number) => {
    const reason = validationNotes[periodId] || 'Annulée';
    if (!reason.trim()) {
      setError('Veuillez entrer une raison pour l\'annulation');
      return;
    }
    try {
      setActionLoading(periodId);
      await commissionService.cancelPeriod(periodId, { reason });
      setActionSuccess('Commission annulée');
      setTimeout(() => setActionSuccess(null), 3000);
      // Refresh periods
      if (selectedApporteur) {
        const periods = await commissionService.listPeriods({ admin_id: selectedApporteur.admin_id });
        setApporteurPeriods(periods);
      }
      // Refresh dashboard
      await loadDashboard();
    } catch (err: any) {
      console.error('Error cancelling commission:', err);
      setError(err.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setActionLoading(null);
    }
  };

  const openDeleteConfirm = (periodId: number) => {
    setPeriodToDelete(periodId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!periodToDelete) return;
    try {
      setActionLoading(periodToDelete);
      await commissionService.deletePeriod(periodToDelete);
      setActionSuccess('Commission supprimée');
      setTimeout(() => setActionSuccess(null), 3000);
      // Refresh periods
      if (selectedApporteur) {
        const periods = await commissionService.listPeriods({ admin_id: selectedApporteur.admin_id });
        setApporteurPeriods(periods);
      }
      // Refresh dashboard
      await loadDashboard();
    } catch (err: any) {
      console.error('Error deleting commission:', err);
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(null);
      setDeleteConfirmOpen(false);
      setPeriodToDelete(null);
    }
  };

  // Filter periods based on status and year
  const filteredPeriods = apporteurPeriods.filter(period => {
    const matchesStatus = periodStatusFilter === 'all' || period.status === periodStatusFilter;
    const periodYear = new Date(period.period_start).getFullYear();
    const matchesYear = periodYearFilter === 'all' || periodYear === periodYearFilter;
    return matchesStatus && matchesYear;
  });

  // Get unique years from periods
  const availableYears = [...new Set(apporteurPeriods.map(p => new Date(p.period_start).getFullYear()))].sort((a, b) => b - a);

  // Open period details popup for apporteur
  const openPeriodDetailsPopup = async (period: CommissionPeriod) => {
    setSelectedPeriodForDetails(period);
    setLoadingSelectedDetails(true);
    try {
      const details = await commissionService.getPeriodDetails(period.id);
      setSelectedPeriodDetails(details);
    } catch (err) {
      console.error('Error loading period details:', err);
      setSelectedPeriodDetails([]);
    } finally {
      setLoadingSelectedDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!dashboard && !superAdminDashboard) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Aucune donnée de commission disponible</p>
        </div>
      </div>
    );
  }

  // Super Admin Dashboard View
  if (isSuperAdmin && superAdminDashboard) {
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard Commissions</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Vue globale des commissions de tous les apporteurs d'affaires
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadDashboard}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </button>
            <button
              onClick={() => setShowCalcModal(true)}
              disabled={calculating}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Calculator className="h-4 w-4" />
              {calculating ? 'Calcul en cours...' : 'Calculer commissions'}
            </button>
          </div>
        </div>

        {/* Calculate Modal */}
        {showCalcModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Calculer les commissions
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Sélectionnez la période pour laquelle calculer les commissions
              </p>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Mois
                  </label>
                  <select
                    value={calcMonth}
                    onChange={(e) => setCalcMonth(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {monthNames.map((name, index) => (
                      <option key={index} value={index + 1}>{name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Année
                  </label>
                  <select
                    value={calcYear}
                    onChange={(e) => setCalcYear(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {[2024, 2025, 2026, 2027].map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowCalcModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCalculateCommissions}
                  disabled={calculating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {calculating ? 'Calcul...' : 'Calculer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Message */}
        {calcSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              {calcSuccess}
            </p>
          </div>
        )}

        {/* Navigation Tabs */}
        {navigationTabs.length > 1 && (
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {navigationTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <Link
                    key={tab.name}
                    to={tab.href}
                    className={cn(
                      'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm',
                      tab.current
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    )}
                  >
                    <Icon className="mr-2 h-5 w-5" />
                    {tab.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        {/* KPIs for Super Admin */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {/* Total Apporteurs */}
          <Card className="p-2 sm:p-4">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Apporteurs</CardTitle>
              <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 hidden sm:block" />
            </CardHeader>
            <CardContent className="p-0 sm:p-0 pt-1">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {superAdminDashboard.total_admins}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 hidden sm:block">
                Actifs
              </p>
            </CardContent>
          </Card>

          {/* Périodes en attente */}
          <Card className="p-2 sm:p-4">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">En attente</CardTitle>
              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600 hidden sm:block" />
            </CardHeader>
            <CardContent className="p-0 sm:p-0 pt-1">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {superAdminDashboard.pending_periods_count}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 truncate">
                {formatCurrency(superAdminDashboard.pending_commissions_amount)}
              </p>
            </CardContent>
          </Card>

          {/* Périodes validées */}
          <Card className="p-2 sm:p-4">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Validées</CardTitle>
              <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 hidden sm:block" />
            </CardHeader>
            <CardContent className="p-0 sm:p-0 pt-1">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                {superAdminDashboard.validated_periods_count}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 truncate">
                {formatCurrency(superAdminDashboard.validated_commissions_amount)}
              </p>
            </CardContent>
          </Card>

          {/* Total payé YTD */}
          <Card className="p-2 sm:p-4">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Payées</CardTitle>
              <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 hidden sm:block" />
            </CardHeader>
            <CardContent className="p-0 sm:p-0 pt-1">
              <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(superAdminDashboard.paid_commissions_ytd)}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 truncate hidden sm:block">
                {formatCurrency(superAdminDashboard.total_commissions_ytd)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Résumé par apporteur */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Résumé par apporteur</CardTitle>
              <div className="flex items-center gap-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {/* View Toggle */}
                <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('table')}
                    className={cn(
                      'p-2 transition-colors',
                      viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 hover:bg-gray-100'
                    )}
                    title="Vue tableau"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={cn(
                      'p-2 transition-colors',
                      viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 hover:bg-gray-100'
                    )}
                    title="Vue liste"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredAdmins.length === 0 ? (
              <p className="text-center text-gray-500 py-4">Aucun apporteur avec des commissions</p>
            ) : viewMode === 'table' ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Apporteur</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">Taux</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">En attente</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600 dark:text-gray-400">Validées</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Payées (année)</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Total (année)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredAdmins.map((admin) => (
                      <tr
                        key={admin.admin_id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                        onClick={() => handleApporteurClick(admin)}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 dark:text-white">{admin.admin_name}</div>
                          <div className="text-xs text-gray-500">{admin.admin_email}</div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline">{admin.current_rate ?? 10}%</Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {admin.pending_count > 0 ? (
                            <span className="text-orange-600 font-medium">
                              {admin.pending_count} ({formatCurrency(admin.pending_amount)})
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {admin.validated_count > 0 ? (
                            <span className="text-blue-600 font-medium">
                              {admin.validated_count} ({formatCurrency(admin.validated_amount)})
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-green-600">
                          {formatCurrency(admin.paid_ytd)}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                          {formatCurrency(admin.total_ytd)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* List View */
              <div className="space-y-3">
                {filteredAdmins.map((admin) => (
                  <div
                    key={admin.admin_id}
                    onClick={() => handleApporteurClick(admin)}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{admin.admin_name}</div>
                          <div className="text-xs text-gray-500">{admin.admin_email}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">{formatCurrency(admin.total_ytd)}</div>
                        <div className="text-xs text-gray-500">Total année</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <Badge variant="outline">{admin.current_rate ?? 10}%</Badge>
                      {admin.pending_count > 0 && (
                        <span className="text-orange-600">
                          {admin.pending_count} en attente ({formatCurrency(admin.pending_amount)})
                        </span>
                      )}
                      {admin.validated_count > 0 && (
                        <span className="text-blue-600">
                          {admin.validated_count} validées ({formatCurrency(admin.validated_amount)})
                        </span>
                      )}
                      {admin.paid_ytd > 0 && (
                        <span className="text-green-600">
                          {formatCurrency(admin.paid_ytd)} payées
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Apporteur Detail Popup */}
        {selectedApporteur && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Popup Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedApporteur.admin_name}</h2>
                    <p className="text-sm text-gray-500">{selectedApporteur.admin_email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedApporteur(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Popup Summary */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700 grid grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">{selectedApporteur.current_rate ?? 10}%</div>
                  <div className="text-xs text-gray-500">Taux actuel</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{formatCurrency(selectedApporteur.pending_amount)}</div>
                  <div className="text-xs text-gray-500">{selectedApporteur.pending_count} en attente</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{formatCurrency(selectedApporteur.validated_amount)}</div>
                  <div className="text-xs text-gray-500">{selectedApporteur.validated_count} validées</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedApporteur.paid_ytd)}</div>
                  <div className="text-xs text-gray-500">Payées (année)</div>
                </div>
              </div>

              {/* Popup Content - Periods List */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Historique des périodes</h3>
                  <div className="flex items-center gap-3">
                    {/* Status Filter */}
                    <select
                      value={periodStatusFilter}
                      onChange={(e) => setPeriodStatusFilter(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                    >
                      <option value="all">Tous les statuts</option>
                      <option value="pending">En attente</option>
                      <option value="validated">Validées</option>
                      <option value="paid">Payées</option>
                      <option value="cancelled">Annulées</option>
                    </select>
                    {/* Year Filter */}
                    <select
                      value={periodYearFilter}
                      onChange={(e) => setPeriodYearFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                      className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
                    >
                      <option value="all">Toutes les années</option>
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {loadingPeriods ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredPeriods.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Aucune période de commission</p>
                ) : (
                  <div className="space-y-3">
                    {filteredPeriods.map((period) => (
                      <div key={period.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        <div
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          onClick={() => togglePeriodDetails(period.id)}
                        >
                          <div className="flex items-center gap-4">
                            <Badge variant={getCommissionStatusColor(period.status) as any}>
                              {getCommissionStatusLabel(period.status)}
                            </Badge>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                {formatPeriod(period.period_start)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {period.total_orders} commandes • {formatCurrency(period.total_sales)} CA
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrency(period.commission_amount)}
                              </div>
                              <div className="text-xs text-gray-500">
                                Taux: {period.commission_rate}%
                                {period.bonus_applied && (
                                  <span className="ml-1 text-green-600">🎉 Bonus +{period.bonus_rate}%</span>
                                )}
                              </div>
                            </div>
                            {expandedPeriod === period.id ? (
                              <ChevronUp className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {expandedPeriod === period.id && (
                          <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                            {loadingDetails === period.id ? (
                              <div className="flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                              </div>
                            ) : periodDetails[period.id]?.length > 0 ? (
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Détails des commandes</div>
                                <table className="w-full text-sm">
                                  <thead className="bg-gray-100 dark:bg-gray-800">
                                    <tr>
                                      <th className="px-3 py-2 text-left">Commande</th>
                                      <th className="px-3 py-2 text-left">Client</th>
                                      <th className="px-3 py-2 text-right">Montant HT</th>
                                      <th className="px-3 py-2 text-right">Commission</th>
                                      <th className="px-3 py-2 text-center">Date paiement</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {periodDetails[period.id].map((detail) => (
                                      <tr key={detail.id}>
                                        <td className="px-3 py-2 flex items-center gap-2">
                                          <FileText className="h-4 w-4 text-gray-400" />
                                          {detail.order_number || `#${detail.purchase_order_id}`}
                                        </td>
                                        <td className="px-3 py-2">{detail.client_name || `Client #${detail.client_id}`}</td>
                                        <td className="px-3 py-2 text-right">{formatCurrency(detail.invoice_amount)}</td>
                                        <td className="px-3 py-2 text-right text-green-600 font-medium">{formatCurrency(detail.commission_amount)}</td>
                                        <td className="px-3 py-2 text-center text-gray-500">
                                          {detail.paid_date ? new Date(detail.paid_date).toLocaleDateString('fr-FR') : '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-center text-gray-500 py-4">Aucun détail disponible</p>
                            )}

                            {/* Action Buttons for pending/validated periods */}
                            {(period.status === 'pending' || period.status === 'validated') && (
                              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="mb-3">
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Notes {period.status === 'pending' ? '(validation)' : '(paiement)'}
                                  </label>
                                  <textarea
                                    value={validationNotes[period.id] || ''}
                                    onChange={(e) => setValidationNotes(prev => ({ ...prev, [period.id]: e.target.value }))}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                                    placeholder={period.status === 'pending' ? 'Notes de validation...' : 'Notes de paiement...'}
                                  />
                                </div>
                                <div className="flex justify-between gap-3">
                                  {/* Delete button - only for pending */}
                                  {period.status === 'pending' && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openDeleteConfirm(period.id); }}
                                      disabled={actionLoading === period.id}
                                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors flex items-center gap-2"
                                      title="Supprimer cette période"
                                    >
                                      <X className="h-4 w-4" />
                                      Supprimer
                                    </button>
                                  )}
                                  <div className="flex gap-3 ml-auto">
                                    {period.status === 'pending' && (
                                      <>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleCancel(period.id); }}
                                          disabled={actionLoading === period.id}
                                          className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center gap-2"
                                        >
                                          <X className="h-4 w-4" />
                                          Annuler
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleValidate(period.id); }}
                                          disabled={actionLoading === period.id}
                                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                                        >
                                          <CheckCircle className="h-4 w-4" />
                                          {actionLoading === period.id ? 'Validation...' : 'Valider'}
                                        </button>
                                      </>
                                    )}
                                    {period.status === 'validated' && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handlePay(period.id); }}
                                        disabled={actionLoading === period.id}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                                      >
                                        <Euro className="h-4 w-4" />
                                        {actionLoading === period.id ? 'Paiement...' : 'Marquer comme payée'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Delete button for cancelled periods */}
                            {period.status === 'cancelled' && (
                              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                                <button
                                  onClick={(e) => { e.stopPropagation(); openDeleteConfirm(period.id); }}
                                  disabled={actionLoading === period.id}
                                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 transition-colors flex items-center gap-2"
                                  title="Supprimer cette période"
                                >
                                  <X className="h-4 w-4" />
                                  Supprimer
                                </button>
                              </div>
                            )}

                            {/* Show validation/payment info */}
                            {period.validated_at && (
                              <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm">
                                <p className="text-blue-800 dark:text-blue-200">
                                  ✓ Validée le {new Date(period.validated_at).toLocaleDateString('fr-FR')}
                                  {period.validation_notes && ` - ${period.validation_notes}`}
                                </p>
                              </div>
                            )}
                            {period.paid_at && (
                              <div className="mt-2 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-sm">
                                <p className="text-green-800 dark:text-green-200">
                                  💰 Payée le {new Date(period.paid_at).toLocaleDateString('fr-FR')}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Success Message */}
              {actionSuccess && (
                <div className="mx-6 mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-green-800 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {actionSuccess}
                  </p>
                </div>
              )}

              {/* Popup Footer */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => setSelectedApporteur(null)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
          title="Supprimer la période de commission"
          description="Êtes-vous sûr de vouloir supprimer cette période de commission ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          isLoading={actionLoading === periodToDelete}
          onConfirm={handleDeleteConfirm}
        />
      </div>
    );
  }

  // Regular Admin/Apporteur Dashboard View
  if (!dashboard) return null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mes Commissions</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Consultez vos commissions mensuelles et votre historique de paiements
        </p>
      </div>

      {/* Navigation Tabs */}
      {navigationTabs.length > 1 && (
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {navigationTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.name}
                  to={tab.href}
                  className={cn(
                    'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm',
                    tab.current
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  )}
                >
                  <Icon className="mr-2 h-5 w-5" />
                  {tab.name}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Mois en cours */}
        <Card className="p-2 sm:p-4">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Ce mois</CardTitle>
            <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 hidden sm:block" />
          </CardHeader>
          <CardContent className="p-0 sm:p-0 pt-1">
            <div className="text-base sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {formatCurrency(dashboard.current_month.estimated_commission)}
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 truncate hidden sm:block">
              {dashboard.current_month.orders_count} cmdes
            </p>
          </CardContent>
        </Card>

        {/* Commissions en attente */}
        <Card className="p-2 sm:p-4">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">En attente</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600 hidden sm:block" />
          </CardHeader>
          <CardContent className="p-0 sm:p-0 pt-1">
            <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
              {dashboard.pending_commissions.length}
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 hidden sm:block">
              À valider
            </p>
          </CardContent>
        </Card>

        {/* Commissions payées YTD */}
        <Card className="p-2 sm:p-4">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Payées</CardTitle>
            <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 hidden sm:block" />
          </CardHeader>
          <CardContent className="p-0 sm:p-0 pt-1">
            <div className="text-base sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {formatCurrency(dashboard.paid_commissions_ytd)}
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 hidden sm:block">
              Perçues
            </p>
          </CardContent>
        </Card>

        {/* Total YTD */}
        <Card className="p-2 sm:p-4">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between pb-1 sm:pb-2 p-0 sm:p-0">
            <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">Total</CardTitle>
            <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600 hidden sm:block" />
          </CardHeader>
          <CardContent className="p-0 sm:p-0 pt-1">
            <div className="text-base sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
              {formatCurrency(dashboard.total_commissions_ytd)}
            </div>
            <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 hidden sm:block">
              YTD
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Historique des commissions avec filtres */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Historique des commissions</CardTitle>
            <div className="flex items-center gap-3">
              {/* Status Filter - all statuses visible */}
              <select
                value={periodStatusFilter}
                onChange={(e) => setPeriodStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="validated">Validées</option>
                <option value="paid">Payées</option>
                <option value="cancelled">Annulées</option>
              </select>
              {/* Year Filter */}
              <select
                value={periodYearFilter}
                onChange={(e) => setPeriodYearFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm"
              >
                <option value="all">Toutes les années</option>
                {[...new Set((dashboard.all_periods || []).map(p => new Date(p.period_start).getFullYear()))].sort((a, b) => b - a).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {/* View Toggle */}
              <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                <button
                  onClick={() => setApporteurViewMode('list')}
                  className={cn(
                    'p-2 transition-colors',
                    apporteurViewMode === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  )}
                  title="Vue liste"
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setApporteurViewMode('table')}
                  className={cn(
                    'p-2 transition-colors',
                    apporteurViewMode === 'table'
                      ? 'bg-blue-600 text-white'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  )}
                  title="Vue tableau"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* List View */}
          {apporteurViewMode === 'list' && (
            <div className="space-y-4">
              {(dashboard.all_periods || [])
                .filter(period => {
                  const matchesStatus = periodStatusFilter === 'all' || period.status === periodStatusFilter;
                  const periodYear = new Date(period.period_start).getFullYear();
                  const matchesYear = periodYearFilter === 'all' || periodYear === periodYearFilter;
                  return matchesStatus && matchesYear;
                })
                .map((period: CommissionPeriod) => (
                  <div
                    key={period.id}
                    className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => openPeriodDetailsPopup(period)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {formatPeriod(period.period_start)}
                          </h3>
                          <Badge variant={getCommissionStatusColor(period.status) as any}>
                            {getCommissionStatusLabel(period.status)}
                          </Badge>
                          {period.bonus_applied && (
                            <span className="text-green-600 text-sm">🎉 Bonus +{period.bonus_rate}%</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {period.total_orders} commandes • {formatCurrency(period.total_sales)} CA • Taux: {period.commission_rate}%
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900 dark:text-white">
                          {formatCurrency(period.commission_amount)}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {period.calculated_at ? new Date(period.calculated_at).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                    </div>

                    {/* Show validation info */}
                    {period.validated_at && (
                      <div className="mt-3 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm">
                        <p className="text-blue-800 dark:text-blue-200">
                          ✓ Validée le {new Date(period.validated_at).toLocaleDateString('fr-FR')}
                          {period.validation_notes && <span className="block mt-1 text-blue-600 dark:text-blue-300">Note: {period.validation_notes}</span>}
                        </p>
                      </div>
                    )}

                    {/* Show payment info */}
                    {period.paid_at && (
                      <div className="mt-2 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-sm">
                        <p className="text-green-800 dark:text-green-200">
                          💰 Payée le {new Date(period.paid_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

              {(dashboard.all_periods || []).filter(period => {
                const matchesStatus = periodStatusFilter === 'all' || period.status === periodStatusFilter;
                const periodYear = new Date(period.period_start).getFullYear();
                const matchesYear = periodYearFilter === 'all' || periodYear === periodYearFilter;
                return matchesStatus && matchesYear;
              }).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Aucune commission trouvée avec ces filtres
                  </div>
                )}
            </div>
          )}

          {/* Table View */}
          {apporteurViewMode === 'table' && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Commandes</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">CA</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Taux</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Validée le</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payée le</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(dashboard.all_periods || [])
                    .filter(period => {
                      const matchesStatus = periodStatusFilter === 'all' || period.status === periodStatusFilter;
                      const periodYear = new Date(period.period_start).getFullYear();
                      const matchesYear = periodYearFilter === 'all' || periodYear === periodYearFilter;
                      return matchesStatus && matchesYear;
                    })
                    .map((period: CommissionPeriod) => (
                      <tr key={period.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer" onClick={() => openPeriodDetailsPopup(period)}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatPeriod(period.period_start)}
                          </span>
                          {period.bonus_applied && (
                            <span className="ml-2 text-green-600 text-xs">🎉 Bonus</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Badge variant={getCommissionStatusColor(period.status) as any}>
                            {getCommissionStatusLabel(period.status)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600 dark:text-gray-400">
                          {period.total_orders}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600 dark:text-gray-400">
                          {formatCurrency(period.total_sales)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-gray-600 dark:text-gray-400">
                          {period.commission_rate}%
                          {period.bonus_applied && <span className="text-green-600"> (+{period.bonus_rate}%)</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right font-bold text-gray-900 dark:text-white">
                          {formatCurrency(period.commission_amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400">
                          {period.validated_at ? new Date(period.validated_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400">
                          {period.paid_at ? new Date(period.paid_at).toLocaleDateString('fr-FR') : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={period.validation_notes || ''}>
                          {period.validation_notes || '-'}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {(dashboard.all_periods || []).filter(period => {
                const matchesStatus = periodStatusFilter === 'all' || period.status === periodStatusFilter;
                const periodYear = new Date(period.period_start).getFullYear();
                const matchesYear = periodYearFilter === 'all' || periodYear === periodYearFilter;
                return matchesStatus && matchesYear;
              }).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Aucune commission trouvée avec ces filtres
                  </div>
                )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Period Details Popup for Apporteur */}
      {selectedPeriodForDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Popup Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Détails de la commission - {formatPeriod(selectedPeriodForDetails.period_start)}
                </h2>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant={getCommissionStatusColor(selectedPeriodForDetails.status) as any}>
                    {getCommissionStatusLabel(selectedPeriodForDetails.status)}
                  </Badge>
                  {selectedPeriodForDetails.bonus_applied && (
                    <span className="text-green-600 text-sm">🎉 Bonus +{selectedPeriodForDetails.bonus_rate}%</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedPeriodForDetails(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Summary Stats */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 grid grid-cols-4 gap-4 bg-gray-50 dark:bg-gray-900/50">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{selectedPeriodForDetails.total_orders}</div>
                <div className="text-xs text-gray-500">Commandes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(selectedPeriodForDetails.total_sales)}</div>
                <div className="text-xs text-gray-500">CA Total</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{selectedPeriodForDetails.commission_rate}%</div>
                <div className="text-xs text-gray-500">Taux</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{formatCurrency(selectedPeriodForDetails.commission_amount)}</div>
                <div className="text-xs text-gray-500">Commission</div>
              </div>
            </div>

            {/* Validation/Payment Info */}
            {(selectedPeriodForDetails.validated_at || selectedPeriodForDetails.paid_at || selectedPeriodForDetails.validation_notes) && (
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-2">
                {selectedPeriodForDetails.validated_at && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm">
                    <p className="text-blue-800 dark:text-blue-200">
                      ✓ Validée le {new Date(selectedPeriodForDetails.validated_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                {selectedPeriodForDetails.paid_at && (
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-sm">
                    <p className="text-green-800 dark:text-green-200">
                      💰 Payée le {new Date(selectedPeriodForDetails.paid_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                )}
                {selectedPeriodForDetails.validation_notes && (
                  <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg text-sm">
                    <p className="text-gray-600 dark:text-gray-300">
                      <span className="font-medium">Note:</span> {selectedPeriodForDetails.validation_notes}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Order Details */}
            <div className="flex-1 overflow-y-auto p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Détails des commandes</h3>

              {loadingSelectedDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : selectedPeriodDetails.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left">Commande</th>
                        <th className="px-4 py-2 text-left">Client</th>
                        <th className="px-4 py-2 text-left">Date paiement</th>
                        <th className="px-4 py-2 text-right">Montant HT</th>
                        <th className="px-4 py-2 text-right">Commission</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {selectedPeriodDetails.map((detail) => (
                        <tr key={detail.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-2 font-medium">{detail.order_number || `#${detail.purchase_order_id}`}</td>
                          <td className="px-4 py-2">{detail.client_name || detail.buyer_name || '-'}</td>
                          <td className="px-4 py-2">{new Date(detail.paid_date).toLocaleDateString('fr-FR')}</td>
                          <td className="px-4 py-2 text-right">{formatCurrency(detail.invoice_amount)}</td>
                          <td className="px-4 py-2 text-right font-medium text-green-600">{formatCurrency(detail.commission_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 dark:bg-gray-700 font-medium">
                      <tr>
                        <td colSpan={3} className="px-4 py-2 text-right">Total</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(selectedPeriodForDetails.total_sales)}</td>
                        <td className="px-4 py-2 text-right text-green-600">{formatCurrency(selectedPeriodForDetails.commission_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">Aucun détail disponible</p>
              )}
            </div>

            {/* Popup Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setSelectedPeriodForDetails(null)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
